<template>
    <div>
        <list-component :items="coupons" @edit="openEditModal" @delete="deleteCoupon" />

        <br />
        <button class="btn btn-primary mb-3" style="float: right;" @click="openAddModal">{{ $t('createNewCoupon') }}</button>

        <modal-component :isVisible="isModalVisible" @close="closeModal">
            <form @submit.prevent="saveCoupon">
                <div v-if="errorMessage" class="alert alert-danger">
                    {{ errorMessage }}
                </div>
                <div class="mb-3 form-check form-switch">
                    <input class="form-check-input" type="checkbox" id="couponActive" v-model="coupon.active">
                    <label class="form-check-label" for="couponActive">{{ $t('active') }}</label>
                </div>
                <div class="mb-3">
                    <label for="couponName" class="form-label">{{ $t('couponReportingName') }}</label>
                    <input type="text" class="form-control" id="couponName" v-model="coupon.name"
                        :placeholder="$t('couponReportingNamePlaceholder')" required maxlength="20">
                </div>
                <div class="mb-3">
                    <label for="couponCode" class="form-label">{{ $t('couponCode') }}</label>
                    <input type="text" class="form-control" id="couponCode" v-model="coupon.code"
                        @input="coupon.code = coupon.code.toUpperCase()" :placeholder="$t('couponCodePlaceholder')" required
                        maxlength="10">
                </div>
                <div class="row mb-3">
                    <div class="col">
                        <label for="couponAmount" class="form-label">{{ $t('couponAmount') }}</label>
                        <input type="number" class="form-control" id="couponAmount" v-model="coupon.amount" required>
                    </div>
                    <div class="col">
                        <label for="couponType" class="form-label">{{ $t('couponType') }}</label>
                        <select class="form-select" id="couponType" v-model="coupon.type" required>
                            <option value="%">{{ $t('percent') }}</option>
                            <option value="$">{{ $t('amountSymbol') }}</option>
                        </select>
                    </div>
                </div>
                <div class="mb-3">
                    <label class="form-label">{{ $t('excludeDepartmentsHelp') }}</label>
                    <div v-if="availableDepartments.length === 0" class="text-muted">
                        {{ $t('noDepartmentsLoaded') }}
                        <div v-if="departmentsLoadError" class="text-danger mt-1">
                            {{ departmentsLoadError }}
                        </div>
                    </div>
                    <div v-else class="dept-list">
                        <div class="form-check" v-for="dept in availableDepartments" :key="dept.ref">
                            <input class="form-check-input" type="checkbox"
                                :id="`dept-${dept.ref}`"
                                :value="dept.ref"
                                v-model="coupon.excluded_department_refs">
                            <label class="form-check-label" :for="`dept-${dept.ref}`">
                                {{ dept.name }}
                            </label>
                        </div>
                    </div>
                    <small class="form-text text-muted">
                        {{ $t('excludeDepartmentsItemsHelp') }}
                    </small>
                </div>
                <div class="d-flex justify-content-between">
                    <button type="button" class="btn btn-outline-secondary me-2" @click="closeModal">{{ $t('cancel') }}</button>
                    <button type="submit" class="btn btn-primary">{{ $t('saveChanges') }}</button>
                </div>
            </form>
        </modal-component>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ListComponent from '@/components/features/admin/ListComponent';
import ModalComponent from '@/components/features/admin/ModalComponent';
import api from "@/utils/api";

export default {
    components: {
        ListComponent,
        ModalComponent
    },
    data() {
        return {
            isModalVisible: false,
            coupon: {
                name: '',
                code: '',
                amount: '',
                type: '%',
                active: true,
                appendDate: false,
                excluded_department_refs: []
            },
            isEditMode: false,
            showTooltip: false,
            errorMessage: null, // Add errorMessage to hold the error returned by the backend
            availableDepartments: [],
            departmentsLoadError: null,
        };
    },
    computed: {
        ...mapState('coupon', ['coupons']),
        ...mapState('admin', ['token', 'activeLocationId'])
    },
    methods: {
        ...mapActions('coupon', ['fetchCoupons', 'updateCoupon', 'createCoupon', 'deleteCoupon']),
        async loadDepartments() {
            try {
                this.departmentsLoadError = null;
                if (!this.token || !this.activeLocationId) return;

                const menuData = await api.callAPI('get', `/admin/menu/${this.activeLocationId}/kiosk`, null, this.token);
                const schedules = menuData?.schedules || [];
                const deptMap = new Map();
                schedules.forEach(schedule => {
                    (schedule.departments || []).forEach(dept => {
                        if (dept?.ref && !deptMap.has(dept.ref)) {
                            deptMap.set(dept.ref, { ref: dept.ref, name: dept.name || dept.ref });
                        }
                    });
                });
                this.availableDepartments = Array.from(deptMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            } catch (error) {
                console.error('Failed to load departments for coupons:', error);
                this.availableDepartments = [];
                this.departmentsLoadError = error?.response?.data?.error || error?.message || this.$t('failedToLoadDepartments');
            }
        },
        openAddModal() {
            this.isEditMode = false;
            this.coupon = { active: true, name: '', code: '', amount: '', type: '%', append_date: false, excluded_department_refs: [] };
            this.isModalVisible = true;
            this.errorMessage = null; // Reset error message
        },
        openEditModal(coupon) {
            this.isEditMode = true;
            this.coupon = { excluded_department_refs: [], ...coupon }; // Ensure field exists for older coupons
            if (!Array.isArray(this.coupon.excluded_department_refs)) this.coupon.excluded_department_refs = [];
            this.isModalVisible = true;
            this.errorMessage = null; // Reset error message
        },
        closeModal() {
            this.isModalVisible = false;
            this.coupon = { name: '', code: '', amount: '', append_date: false, excluded_department_refs: [] };
            this.errorMessage = null; // Reset error message
        },
        async saveCoupon() {
            this.errorMessage = null; // Clear previous errors

            try {
                if (this.isEditMode) {
                    await this.updateCoupon({ id: this.coupon.id, couponData: this.coupon });
                } else {
                    await this.createCoupon(this.coupon);
                }
                this.closeModal();
            } catch (error) {
                this.errorMessage = error.response?.data?.error || this.$t('errorOccurred'); // Display the error message
            }
        },
        getTodayDate() {
            const today = new Date();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            return month + day;
        }
    },
    created() {
        this.fetchCoupons();
        this.loadDepartments();
    },
    watch: {
        token() {
            this.loadDepartments();
        },
        activeLocationId() {
            this.loadDepartments();
        }
    }
};
</script>

<style scoped>
/* Add any scoped styles for the Coupons component here */
.btn-success {
    margin-bottom: 1rem;
}

.dept-list {
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    padding: 10px;
    background: #fff;
}

.modal-content {
    padding: 2rem;
}

.info-icon {
    margin-left: 5px;
    cursor: pointer;
}

.tooltip-text {
    position: absolute;
    background-color: #f9f9f9;
    border: 1px solid #ddd;
    padding: 5px;
    border-radius: 5px;
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
    width: 200px;
    z-index: 1000;
}
</style>
